// src/App.tsx

import React, { useEffect, useRef, useState } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Header from "./components/layouts/Header";
import Sidebar from "./components/layouts/Sidebar";
import { ToastProvider } from "./components/ui/Toast";
import { menuItems } from "./config/menuConfig";
import Login from "./pages/auth/Login";
import AppRoutes from "./routes/AppRoutes";
import { fetchData } from "./services/apiService";
import { CurrentUser, DashboardData, RouterInfo } from "./types";
import {
  clearMikrotikToken,
  generateMikrotikToken,
  saveMikrotikToken,
} from "./utils/mikrotikToken";

const defaultDashboardData: DashboardData = {
  customers: {},
  bandwidth: {},
  revenue: {},
  alerts: [],
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const token = localStorage.getItem("token");

  const loadedTokenRef = useRef<string | null>(null);

  const isPortal =
    location.pathname.startsWith("/portal") ||
    location.pathname.startsWith("/my-account");

  const isCustomerCarePage = location.pathname.startsWith("/customer-care");

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [dashboardData] = useState<DashboardData>(defaultDashboardData);

  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [selectedRouter, setSelectedRouter] = useState<RouterInfo | null>(null);
  const [userLoading, setUserLoading] = useState<boolean>(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("userRole");

    clearMikrotikToken();

    loadedTokenRef.current = null;

    setCurrentUser(null);
    setSelectedRouter(null);

    navigate("/login", { replace: true });
  };

  useEffect(() => {
    if (!token) {
      loadedTokenRef.current = null;
      setCurrentUser(null);
      setSelectedRouter(null);
      clearMikrotikToken();
      return;
    }

    if (isPortal) return;

    // Prevent calling /auth/me again on normal re-renders.
    if (loadedTokenRef.current === token && currentUser) return;

    let cancelled = false;

    const loadUser = async () => {
      try {
        setUserLoading(true);

        const user: CurrentUser = await fetchData("/auth/me");

        if (cancelled) return;

        loadedTokenRef.current = token;

        setCurrentUser(user);

        localStorage.setItem("user", JSON.stringify(user));

        if (user?.role?.name) {
          localStorage.setItem("userRole", user.role.name);
        }

        if (user.routers && user.routers.length > 0) {
          const firstRouter = user.routers[0];

          setSelectedRouter(firstRouter);

          const host = firstRouter.ip_address?.split("/")?.[0];

          if (
            host &&
            firstRouter.mikrotik_username &&
            firstRouter.mikrotik_password
          ) {
            try {
              const mikrotikToken = await generateMikrotikToken({
                host,
                username: firstRouter.mikrotik_username,
                password: firstRouter.mikrotik_password,
              });

              if (!cancelled) {
                saveMikrotikToken(mikrotikToken);
              }
            } catch (error) {
              console.error("Failed to generate MikroTik token:", error);
              clearMikrotikToken();
            }
          } else {
            clearMikrotikToken();
          }
        } else {
          setSelectedRouter(null);
          clearMikrotikToken();
        }
      } catch (error) {
        if (!cancelled) {
          handleLogout();
        }
      } finally {
        if (!cancelled) {
          setUserLoading(false);
        }
      }
    };

    loadUser();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isPortal]);

  if (isPortal || isCustomerCarePage) {
    return (
      <main className="min-h-screen w-full bg-[#eef3f8]">
        <AppRoutes data={dashboardData} />
      </main>
    );
  }

  // Admin: not logged in.
  if (!token) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Admin: loading /auth/me.
  if (userLoading && !currentUser) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#eef3f8]">
        <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden px-6 py-10 text-center">
            <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
            </div>

            <p className="relative mt-4 text-[15px] font-black text-slate-900">
              Loading your account…
            </p>

            <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
              Please wait while we verify your session.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#eef3f8]">
      <Sidebar isOpen={sidebarOpen} menuItems={menuItems} />

      <div className="flex flex-1 flex-col overflow-hidden">
        <Header
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
          title=""
          user={currentUser}
          selectedRouter={selectedRouter}
          onSelectRouter={() => {}}
          onLogout={handleLogout}
        />

        <main className="flex-1 overflow-y-auto p-6">
          <AppRoutes data={dashboardData} />
        </main>
      </div>
    </div>
  );
};

const App: React.FC = () => (
  <Router>
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  </Router>
);

export default App;
